import { Component, computed, effect, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { MenuService } from '../../../menu.service';
import { filterSearchGroups, filterSections, flattenSearchResults, getSearchGroups, toSuperSectionVM } from '../../../menu.utils';
import { ComboRegistryService } from '../../../../services/combo-registry.service';
import { DataService } from '../../../../services/data.service';

@Component({
  selector: 'app-super-section-grid',
  templateUrl: './super-section-grid.component.html',
  styleUrl: './super-section-grid.component.scss',
  standalone: false,
})
export class SuperSectionGridComponent {
  private menuService = inject(MenuService);
  private comboRegistry = inject(ComboRegistryService);
  private dataService = inject(DataService);

  searchPlaceholder = computed(() => this.dataService.restaurant()?.is_parent ? 'ACTIONS.SEARCH_PLACEHOLDER' : 'MENU.SEARCH');

  private comboGroupIds = computed(() => new Set(this.comboRegistry.getGroups().map(group => group.groupId)));
  isCombo = (sectionId: string): boolean => this.comboGroupIds().has(sectionId);

  // Measured by the parent page from its own sticky header (top-bar) - a fixed constant
  // here would go stale the moment it changes height.
  topBarHeight = input(40);

  // Real rendered height of the cart-block + combo-guide-block sticky group below the
  // top bar, so the search box (which sticks right under it) offsets against what's
  // actually on screen instead of a guess - the guide block's height varies with how
  // many combos are active, and cart-block collapses to nothing when the cart is empty.
  private cartInfoEl = viewChild<ElementRef<HTMLDivElement>>('cartInfo');
  cartInfoHeight = signal(0);

  searchInput = signal<string | null>(null);
  isSearchFocused = signal(false);
  isSearchActive = computed(() => this.isSearchFocused() || !!this.searchInput());
  searchActiveChange = output<boolean>();

  // top-bar is hidden while searching, so sticky elements below it no longer need to offset for it
  effectiveTopBarHeight = computed(() => this.isSearchActive() ? 0 : this.topBarHeight());

  superSections = this.menuService.superSections;

  private sections = computed(() => this.menuService.menu()?.sections ?? []);

  sectionsToDisplay = computed(() => {
    const query = this.searchInput();
    return query ? filterSections(this.sections(), query).map(toSuperSectionVM) : this.superSections();
  });

  // Flat list, no headings - each result carries its own subSectionId (for correct combo
  // tagging) and only shows a section badge when this exact item is ambiguous (also found
  // elsewhere), rather than grouping every result under a heading.
  searchResults = computed(() => {
    const query = this.searchInput();
    return query ? flattenSearchResults(filterSearchGroups(getSearchGroups(this.sections()), query), query) : [];
  });

  constructor() {
    effect(() => this.searchActiveChange.emit(this.isSearchActive()));

    effect((onCleanup) => {
      const el = this.cartInfoEl()?.nativeElement;
      if (!el) return;

      const observer = new ResizeObserver(() => this.cartInfoHeight.set(el.offsetHeight));
      observer.observe(el);
      this.cartInfoHeight.set(el.offsetHeight);
      onCleanup(() => observer.disconnect());
    });
  }

  clearSearch(input: HTMLInputElement): void {
    this.searchInput.set(null);
    input.blur();
  }
}
